import { logger } from '@/sentry.config'
import { pdfjsLib } from '@/utils/pdfjs'

/**
 * Opens a PDF in a new tab by **painting its pages ourselves** and adding our
 * own Download and Print controls.
 *
 * Epic's embedded browser has no PDF plugin, so nothing that relies on the
 * browser's native viewer works there: a ``blob:`` URL, a ``data:`` URI
 * ``<embed>``, or a served ``.pdf`` all render a blank tab. The only thing that
 * displays is pixels we paint ourselves, which is exactly what the inline
 * preview already does with pdfjs.
 *
 * So the spawned tab gets an ``about:blank`` document (same origin as its
 * opener, so we can write to it) holding a small toolbar plus one canvas per
 * page, painted from the bytes we hold. Download builds a ``blob:`` URL inside
 * the spawned document (same-origin, so it resolves) and clicks an anchor;
 * Print calls ``window.print()`` on the painted canvases. Both stay disabled
 * until every page is painted so Print can never send a partial report.
 *
 * The bytes are kept for the lifetime of every spawned tab so a reload — which
 * drops everything we wrote into ``about:blank`` — can be repainted.
 */

const PAGE_ROOT_ID = 'pdf-bytes-page-root'
const TOOLBAR_ID = 'pdf-bytes-toolbar'
const PAGE_CLASS = 'pdf-bytes-page'
const STATUS_CLASS = 'pdf-bytes-status'

/** Page size on screen, in CSS pixels per PDF point (1.5 ≈ 108 dpi). */
const PAGE_SCALE = 1.5

/**
 * Pages are rasterized at ``PAGE_SCALE * SUPERSAMPLE`` and displayed at
 * ``PAGE_SCALE``, so text stays sharp on HiDPI screens and when printed.
 */
const SUPERSAMPLE = 2

/**
 * How often spawned tabs are checked for being closed or reloaded. A reload
 * drops everything we wrote (``about:blank`` has nothing to re-fetch), so the
 * check doubles as the repaint trigger.
 */
const WINDOW_WATCH_MS = 1_000

const SHELL_CSS = `
html, body { margin: 0; padding: 0; background: #525659; }
#${TOOLBAR_ID} {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  background: #323639;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}
#${TOOLBAR_ID} button {
  font: 500 13px system-ui, -apple-system, sans-serif;
  color: #f3f4f6;
  background: #4b5563;
  border: 0;
  border-radius: 4px;
  padding: 6px 14px;
  cursor: pointer;
}
#${TOOLBAR_ID} button:hover { background: #6b7280; }
#${TOOLBAR_ID} button:disabled { opacity: 0.5; cursor: default; }
#${TOOLBAR_ID} button:disabled:hover { background: #4b5563; }
#${PAGE_ROOT_ID} {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px;
}
.${STATUS_CLASS} { color: #f3f4f6; font: 14px system-ui, -apple-system, sans-serif; }
.${PAGE_CLASS} { background: #fff; box-shadow: 0 1px 5px rgba(0, 0, 0, 0.5); }
.${PAGE_CLASS} canvas { display: block; }
@media print {
  html, body { background: #fff; }
  #${TOOLBAR_ID} { display: none; }
  #${PAGE_ROOT_ID} { display: block; gap: 0; padding: 0; }
  .${PAGE_CLASS} { box-shadow: none; break-inside: avoid; break-after: page; }
  .${PAGE_CLASS}:last-child { break-after: auto; }
  /* Each page fills the sheet's printable width instead of keeping its fixed
     on-screen pixel width, which the print scaler would otherwise clip. */
  .${PAGE_CLASS} canvas { width: 100%; height: auto; }
}
`

interface SpawnedWindow {
  win: Window
  /** Kept for the tab's lifetime so a reload can be repainted and Download can
   * rebuild its blob. */
  pdfBytes: Uint8Array
  filename: string
  /** Cleared after the first paint so a reload does not re-open the print dialog. */
  printWhenReady: boolean
  painting: boolean
  /**
   * True once every page has been painted. The toolbar's Print/Download stay
   * disabled until then: Print on a partially-painted document would send an
   * incomplete report, and the status node (which said "Rendering PDF…") is
   * removed after the first page, so it cannot signal "not ready" on its own.
   */
  painted: boolean
  /** Toolbar controls, toggled as paint starts/finishes. Absent before the first
   * shell is built. */
  controls: { print: HTMLButtonElement; download: HTMLButtonElement } | null
}

const spawnedWindows: SpawnedWindow[] = []

let watchTimer: ReturnType<typeof setInterval> | null = null

const stopWatch = () => {
  if (watchTimer !== null) {
    clearInterval(watchTimer)
    watchTimer = null
  }
}

/**
 * Downloads ``pdfBytes`` as a PDF named ``filename`` using ``win`` as the
 * execution context. Works from both the calling page (pass ``window``) and a
 * spawned tab: the blob and its URL are created against ``win.URL`` so they
 * resolve in the document that will consume them. ``msSaveBlob`` covers legacy
 * embedded controls that block anchor-driven downloads.
 */
export const downloadPdfBytes = (win: Window, pdfBytes: Uint8Array, filename: string): void => {
  try {
    const blob = new Blob([pdfBytes.slice(0)], { type: 'application/pdf' })

    const legacyNavigator = win.navigator as Navigator & {
      msSaveBlob?: (blob: Blob, defaultName: string) => boolean
    }
    if (typeof legacyNavigator.msSaveBlob === 'function') {
      legacyNavigator.msSaveBlob(blob, filename)
      return
    }

    // `win.URL` is the document's own URL factory, so the object URL resolves
    // in the origin that consumes it. Fall back to the opener's URL when a
    // window somehow lacks its own (jsdom-detached documents in tests).
    const winUrl = (win as Window & { URL?: typeof URL }).URL ?? URL
    const url = winUrl.createObjectURL(blob)
    const anchor = win.document.createElement('a')
    anchor.href = url
    anchor.download = filename
    win.document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    // The download has been handed to the browser by the time click() returns;
    // release the URL on the next tick so the transfer is not cut short.
    win.setTimeout(() => {
      try {
        winUrl.revokeObjectURL(url)
      } catch {
        // Tab closed before the revoke ran; nothing to release.
      }
    }, 0)
  } catch (error) {
    logger.error('PDF download failed', {
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

const downloadFromWindow = (entry: SpawnedWindow) => {
  downloadPdfBytes(entry.win, entry.pdfBytes, entry.filename)
}

const requestPrint = (win: Window) => {
  try {
    win.focus?.()
    win.print?.()
  } catch (error) {
    logger.error('PDF print invocation failed', {
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

/** Enables or disables the toolbar controls; a no-op before the shell exists. */
const setControlsEnabled = (entry: SpawnedWindow, enabled: boolean) => {
  if (!entry.controls) return
  entry.controls.print.disabled = !enabled
  entry.controls.download.disabled = !enabled
}

/** Replaces the spawned document's contents with a toolbar + empty page shell. */
const buildShell = (entry: SpawnedWindow) => {
  const doc = entry.win.document

  const style = doc.createElement('style')
  style.textContent = SHELL_CSS
  doc.head.replaceChildren(style)
  // After the head is rebuilt: the title lives in an element that
  // `replaceChildren` would otherwise have dropped.
  doc.title = entry.filename

  const toolbar = doc.createElement('div')
  toolbar.id = TOOLBAR_ID

  const downloadButton = doc.createElement('button')
  downloadButton.type = 'button'
  downloadButton.textContent = 'Download'
  // Guard the handler too, not just the disabled attribute: the bytes are
  // always complete, but keeping both controls inert until the render finishes
  // is one consistent rule and cannot be bypassed by re-enabling the element.
  downloadButton.addEventListener('click', () => {
    if (entry.painted) downloadFromWindow(entry)
  })

  const printButton = doc.createElement('button')
  printButton.type = 'button'
  printButton.textContent = 'Print'
  // Print must never fire before every page is painted, or it sends a partial
  // document. Gate on `painted` in the handler as well as the disabled attr.
  printButton.addEventListener('click', () => {
    if (entry.painted) requestPrint(entry.win)
  })

  toolbar.append(downloadButton, printButton)

  entry.controls = { print: printButton, download: downloadButton }
  // A fresh shell means the pages are gone (first paint or a reload repaint),
  // so the controls start disabled until this paint completes.
  setControlsEnabled(entry, false)

  const status = doc.createElement('p')
  status.className = STATUS_CLASS
  status.textContent = 'Rendering PDF…'

  const root = doc.createElement('div')
  root.id = PAGE_ROOT_ID
  root.appendChild(status)

  doc.body.replaceChildren(toolbar, root)

  return { root, status }
}

const showShellMessage = (win: Window, message: string) => {
  try {
    const doc = win.document
    let status = doc.querySelector(`.${STATUS_CLASS}`)
    if (!status) {
      // The status node is removed once the first page paints, so a failure on
      // a later page has nothing to update. Re-create it (into the page root
      // when present, else the body) so the tab always shows a failure state
      // instead of a silent partial document.
      status = doc.createElement('p')
      status.className = STATUS_CLASS
      const host = doc.getElementById(PAGE_ROOT_ID) ?? doc.body
      host?.appendChild(status)
    }
    if (status) status.textContent = message
  } catch {
    // Tab closed or navigated away mid-render; nothing to report into.
  }
}

/**
 * Copies a page rasterized in this document into a canvas belonging to the
 * spawned document. pdfjs draws into a canvas from its own document (it reaches
 * for globals while rendering), so the cross-document hand-off is a plain
 * same-origin ``drawImage``.
 */
const appendPageCanvas = (win: Window, root: Element, source: HTMLCanvasElement) => {
  const page = win.document.createElement('div')
  page.className = PAGE_CLASS

  const target = win.document.createElement('canvas')
  target.width = source.width
  target.height = source.height
  target.style.width = `${Math.round(source.width / SUPERSAMPLE)}px`
  target.style.height = `${Math.round(source.height / SUPERSAMPLE)}px`

  const context = target.getContext('2d')
  if (!context) throw new Error('Canvas 2D context unavailable in the new window')
  context.drawImage(source, 0, 0)

  page.appendChild(target)
  root.appendChild(page)
}

const paintPdfWindow = async (entry: SpawnedWindow): Promise<void> => {
  entry.painting = true
  // This paint starts from an empty shell, so the pages are not there yet:
  // block the toolbar until the loop below finishes.
  entry.painted = false
  // Held at this scope so `finally` releases the worker/page resources on every
  // exit path — success, an early return when the tab closes mid-render, or a
  // render failure. pdfjs keeps a document (and its worker transport) alive
  // until `destroy()` is called; without this, every open/print/reload leaks a
  // document for the rest of the session.
  let pdf: pdfjsLib.PDFDocumentProxy | null = null
  try {
    const { root, status } = buildShell(entry)

    // pdfjs transfers the buffer it is given to its worker, which detaches it.
    // Render from a copy so the tab's bytes survive for the next repaint.
    pdf = await pdfjsLib.getDocument({ data: entry.pdfBytes.slice(0) }).promise

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      if (entry.win.closed) return

      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: PAGE_SCALE * SUPERSAMPLE })

      const source = document.createElement('canvas')
      source.width = Math.ceil(viewport.width)
      source.height = Math.ceil(viewport.height)
      const sourceContext = source.getContext('2d')
      if (!sourceContext) throw new Error('Canvas 2D context unavailable')

      // `intent: 'print'` is what actually unblocks the new-tab render. For its
      // default (on-screen) intent pdfjs schedules each rendering chunk with
      // `requestAnimationFrame`, and rAF is paused for a hidden document —
      // opening the tab with `_blank` backgrounds this opener, so the render
      // (which runs here, in the opener) stalled until the user switched tabs.
      // Print intent schedules chunks immediately instead of via rAF, so the
      // page rasterizes to completion no matter which tab is focused. It also
      // renders without on-screen-only downscaling, which suits our supersample.
      await page.render({ canvasContext: sourceContext, viewport, canvas: source, intent: 'print' }).promise

      // Free the page's rasterization/operator-list buffers now that it is
      // copied into the tab; we never revisit a page within a single paint.
      page.cleanup()

      if (entry.win.closed) return
      appendPageCanvas(entry.win, root, source)
      status.remove()
    }

    // Every page is painted: the document is complete, so the toolbar's
    // Print/Download can safely act on it.
    entry.painted = true
    setControlsEnabled(entry, true)

    if (entry.printWhenReady) {
      entry.printWhenReady = false
      requestPrint(entry.win)
    }
  } catch (error) {
    showShellMessage(entry.win, 'This PDF could not be rendered.')
    throw error
  } finally {
    // Release the document and its worker transport. A reload repaints from the
    // retained bytes via a fresh getDocument, so nothing here needs to outlive
    // the paint.
    if (pdf) {
      void pdf.destroy().catch((error: unknown) => {
        logger.error('PDF document cleanup failed', {
          error: error instanceof Error ? error.message : String(error),
        })
      })
    }
    entry.painting = false
  }
}

const watchSpawnedWindows = () => {
  for (let i = spawnedWindows.length - 1; i >= 0; i--) {
    const entry = spawnedWindows[i]

    if (entry.win.closed) {
      spawnedWindows.splice(i, 1)
      continue
    }
    if (entry.painting) continue

    try {
      // A reloaded tab comes back empty: about:blank has nothing to re-fetch,
      // so the page root we wrote is gone and has to be painted again.
      if (entry.win.document.getElementById(PAGE_ROOT_ID)) continue
    } catch {
      // The user navigated the tab somewhere cross-origin; it is no longer ours.
      spawnedWindows.splice(i, 1)
      continue
    }

    void paintPdfWindow(entry).catch((error: unknown) => {
      logger.error('PDF repaint after tab reload failed', {
        error: error instanceof Error ? error.message : String(error),
      })
    })
  }

  if (spawnedWindows.length === 0) {
    stopWatch()
  }
}

const trackSpawnedWindow = (entry: SpawnedWindow) => {
  spawnedWindows.push(entry)
  if (watchTimer === null) {
    watchTimer = setInterval(watchSpawnedWindows, WINDOW_WATCH_MS)
  }
}

const forgetSpawnedWindows = () => {
  spawnedWindows.length = 0
  stopWatch()
}

// Once this document goes away we can no longer repaint anything, and the tabs
// we spawned keep the canvases they already have.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', forgetSpawnedWindows)
}

/**
 * Opens the blank tab that {@link renderPdfBytesInWindow} paints into.
 *
 * Must be called synchronously from the user gesture that asked for it, or the
 * browser treats it as an unsolicited popup and blocks it. Callers that only
 * have the bytes after an ``await`` should open the tab first and render into it
 * once the bytes arrive.
 *
 * Returns ``null`` when the popup was blocked.
 */
export const openBlankPdfWindow = (): Window | null => window.open('', '_blank')

/**
 * Paints ``pdfBytes`` into ``win`` with a Download/Print toolbar and keeps the
 * tab painted until it closes.
 *
 * Rejects if the PDF cannot be rendered, or if the spawned document cannot be
 * written to (an Epic iframe sandboxed without ``allow-popups-to-escape-sandbox``
 * hands back a tab on an opaque origin).
 */
export const renderPdfBytesInWindow = (
  win: Window,
  pdfBytes: Uint8Array,
  options: { filename?: string; print?: boolean } = {}
): Promise<void> => {
  const entry: SpawnedWindow = {
    win,
    pdfBytes,
    filename: options.filename ?? 'document.pdf',
    printWhenReady: options.print ?? false,
    painting: false,
    painted: false,
    controls: null,
  }
  trackSpawnedWindow(entry)
  return paintPdfWindow(entry)
}
